import { useLanguage } from "@/contexts/LanguageContext";
import logoImg from "@/assets/logo-salzburg-taxi.png?format=webp&quality=80";
import LocalizedLink from "./LocalizedLink";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";
import { getLegalLinks } from "@/data/legalNav";

const SiteFooter = () => {
  const { t, lang } = useLanguage();
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const legalLinks = getLegalLinks(lang);

  return (
    <footer className="bg-secondary text-secondary-foreground pt-16 pb-8">
      <div className="container">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          <div>
            <LocalizedLink to="/" className="flex items-center gap-3 mb-4">
              <img src={logoImg} alt="Taxi Salzburg" className="h-36 object-contain" />
            </LocalizedLink>
            <p className="text-secondary-foreground/80 text-sm leading-relaxed max-w-xs">
              {t.footer.description}
            </p>
            <div className="mt-4 space-y-1 text-sm text-secondary-foreground/80">
              <p dir="ltr">
                📞{" "}
                <a href="tel:+436606521071" className="hover:text-primary">
                  +43 660 6521071
                </a>
              </p>
              <p>
                ✉️{" "}
                <a href="mailto:office@taxisalzburg.net" className="hover:text-primary">
                  office@taxisalzburg.net
                </a>
              </p>
              <p>
                🌐{" "}
                <a href="https://www.taxisalzburg.net" className="hover:text-primary">
                  www.taxisalzburg.net
                </a>
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold text-primary mb-4">{t.footer.quickLinks}</h4>
            <ul className="space-y-2.5">
              {[
                { href: "/", label: t.nav.home },
                { href: "/services", label: t.nav.services },
                { href: "/transfers", label: t.nav.transfers },
                { href: "/fleet", label: t.nav.fleet },
                { href: "/booking", label: t.nav.booking },
                { href: "/contact", label: t.nav.contact },
              ].map((l) => (
                <li key={l.href}>
                  <LocalizedLink
                    to={l.href}
                    className="text-secondary-foreground/80 hover:text-primary transition-colors text-sm"
                  >
                    {l.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold text-primary mb-4">{t.footer.services}</h4>
            <ul className="space-y-2.5">
              {[
                { label: t.footer.airportTransfer, href: '/airport-transfer' },
                { label: t.footer.cityTransport, href: '/taxi-salzburg-stadt' },
                { label: t.footer.intercityTransfer, href: '/transferservice' },
                { label: t.footer.tours, href: '/tours' },
                { label: t.footer.corporate, href: '/services/business-taxi' },
              ].map((s) => (
                <li key={s.href}>
                  <LocalizedLink to={s.href} className="text-secondary-foreground/80 hover:text-primary transition-colors text-sm">{s.label}</LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-lg font-semibold text-primary mb-4">{t.footer.popularTransfers}</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                { label: 'Salzburg → Hallstatt', href: '/transfer/taxi-salzburg-hallstatt' },
                { label: 'Salzburg → Munich', href: '/transfer/taxi-salzburg-muenchen' },
                { label: 'Salzburg → Vienna', href: '/transfer/taxi-salzburg-wien' },
                { label: 'Salzburg → Innsbruck', href: '/transfer/taxi-salzburg-innsbruck' },
                { label: 'Salzburg Airport Transfer', href: '/airport-transfer' },
              ].map((l) => (
                <li key={l.href}>
                  <LocalizedLink to={l.href} className="text-secondary-foreground/80 hover:text-primary transition-colors">{l.label}</LocalizedLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-secondary-foreground/10 pt-6 text-center">
          <p className="text-secondary-foreground/80 text-sm">
            © 2010-{new Date().getFullYear()} Taxi Salzburg ·{" "}
            <a href="https://www.taxisalzburg.net" className="hover:text-primary">
              www.taxisalzburg.net
            </a>{" "}
            ·{" "}
            <button
              onClick={() => setDisclaimerOpen(true)}
              className="hover:text-primary underline underline-offset-2 transition-colors cursor-pointer"
            >
              {t.footer.rights}
            </button>
          </p>
        </div>
      </div>

      <Dialog open={disclaimerOpen} onOpenChange={setDisclaimerOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <AlertTriangle className="w-5 h-5 text-primary" />
              {t.footer.disclaimerTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground">
            <p>{t.footer.disclaimerText1}</p>
            <p>{t.footer.disclaimerText2}</p>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
};

export default SiteFooter;
